import { useEffect, useState } from 'react';

import { useRouter } from 'next/router';

export const INSPECTION_BANNER_EXPERIMENT_KEY = 'growth-25-12-inspection-banner';

/**
 * LocalStorage key for storing the experiment variant.
 * This allows us to pass the variant to the backend when creating a seller input
 * so it can be stored on the lead for Iterable attribution.
 */
export const INSPECTION_BANNER_VARIANT_STORAGE_KEY = 'inspection-banner-experiment-variant';

export type InspectionBannerVariant = 'control' | 'treatment' | null;

interface UseInspectionBannerExperimentResult {
  variant: InspectionBannerVariant;
  isLoading: boolean;
}

/**
 * Get the stored inspection banner experiment variant from localStorage.
 * This can be used when creating a seller input to pass the variant to the backend
 * for Iterable attribution tracking.
 */
export function getStoredInspectionBannerVariant(): InspectionBannerVariant {
  if (typeof window === 'undefined') {
    return null;
  }
  const stored = localStorage.getItem(INSPECTION_BANNER_VARIANT_STORAGE_KEY);
  if (stored === 'treatment' || stored === 'control') {
    return stored;
  }
  return null;
}

/**
 * Hook to get the inspection banner experiment variant from query params.
 * Returns 'treatment' to show the banner, 'control' for no banner,
 * or null while loading.
 *
 * The variant is bucketed at the Cloudflare Worker level using Amplitude local evaluation
 * and passed to Cosmos via the `inspection_banner_variant` query param.
 * Impression tracking is handled automatically by the CF Worker.
 *
 * The variant is also stored in localStorage so it can be passed to the backend
 * when creating a seller input for Iterable attribution tracking.
 */
export function useInspectionBannerExperiment(): UseInspectionBannerExperimentResult {
  const router = useRouter();
  const [variant, setVariant] = useState<InspectionBannerVariant>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Wait for router to be ready (query params populated)
    if (!router.isReady) {
      return;
    }

    // Read variant from query param (set by Cloudflare Worker)
    const queryVariant = router.query.inspection_banner_variant as string | undefined;
    const resolvedVariant: InspectionBannerVariant =
      queryVariant === 'treatment' ? 'treatment' : 'control';

    setVariant(resolvedVariant);
    setIsLoading(false);

    // Store variant in localStorage for backend attribution
    localStorage.setItem(INSPECTION_BANNER_VARIANT_STORAGE_KEY, resolvedVariant);
  }, [router.isReady, router.query.inspection_banner_variant]);

  return {
    variant,
    isLoading,
  };
}
